import invariant from 'invariant';
import * as React from 'react';

import createChainedFunction from 'utils/createChainedFunction';
import KeyConstants from 'constants/KeyConstants';
import Layer from 'components/Layer';
import LayerTrigger from 'components/LayerTrigger';
import List from 'components/List';
import LoadingIndicator from 'components/LoadingIndicator';
import Text from 'components/Text';
import { ALL_TOKENS_ITEM, HEADER_ITEM_ID } from 'components/Tokenizer';
import { compact } from 'lodash';

import type { TokenItem, HeaderItem } from 'components/Tokenizer';
// eslint-disable-next-line no-unused-vars
import type { ReactElement, KeyboardEvent } from 'react';

class Typeahead extends React.Component<
  {
    results: any[];
    query: string;
    // Function that renders the list of results
    listRenderer: (b: any, a: boolean) => React.ReactElement<any>;
    // Function that renders a result
    itemRenderer: (d: TokenItem, c: boolean, b: string, a: Function) => React.ReactElement<any>;
    // Function that renders a "Select all" item
    selectAllItemRenderer?: (b: boolean, a: Function) => React.ReactElement<any>;
    // Function that renders a no more items prompt
    noMoreItemsRenderer: (a: void) => React.ReactElement<any> | undefined | null;
    // Function that renders a no results prompt
    noResultsRenderer: (a: string) => React.ReactElement<any> | undefined | null;
    // Function that renders a section header
    headerRenderer?: (a: HeaderItem) => ReactElement<any>;
    // Show the list of results inline rather than in a layer
    inline: boolean;
    // Callback when an item is selected
    onSelect: (a: TokenItem) => void;
    // Callback when the "Select all" item is selected
    onSelectAll: () => void;
    // Hide the results list
    hideResults: boolean;
    onHighlightIndexChange: (a: number) => void;
    onEnter: (a: void) => void;
    className?: string | null;
    children?: ReactElement<any>;
    loading?: boolean;
    manageLayer: boolean;
    useCustomSelectionHandler: boolean;
  },
  {
    highlightIndex: number;
  }
> {
  static defaultProps = {
    listRenderer: (items: TokenItem[], loading: boolean) => {
      return (
        // @ts-expect-error ts-migrate(2769) FIXME: No overload matches this call.
        <List>
          {items}
          {loading && <LoadingIndicator />}
        </List>
      );
    },
    itemRenderer: (item: TokenItem, highlighted: boolean, query: string, onSelect: () => void) => {
      return (
        <Text onClick={onSelect} weight={highlighted ? 'bold' : 'normal'}>
          {item}
        </Text>
      );
    },
    noMoreItemsRenderer: () => null,
    noResultsRenderer: (_str: string) => null,
    hideResults: false,
    onHighlightIndexChange: (_index: number) => undefined,
    onEnter: () => undefined,
    manageLayer: true,
    useCustomSelectionHandler: false,
  };

  state = {
    highlightIndex: -1,
  };

  componentDidMount() {
    if (!this.props.inline) {
      // Hide the layer on window resize
      window.addEventListener('resize', this.hideLayer.bind(this), false);
    }
  }

  componentWillReceiveProps(nextProps: any) {
    if (this.props.query !== nextProps.query) {
      if (nextProps.query === '') {
        this.setState({ highlightIndex: -1 });
      } else {
        this.setState({ highlightIndex: 0 });
      }
    }
  }

  shouldComponentUpdate(nextProps: any, nextState: any) {
    const propsChanged =
      this.props.query !== nextProps.query ||
      this.props.results.length !== nextProps.results.length ||
      this.props.children !== nextProps.children;
    const stateChanged = this.state.highlightIndex !== nextState.highlightIndex;
    return propsChanged || stateChanged;
  }

  componentDidUpdate(prevProps: any, prevState: any) {
    if (this.refs.trigger) {
      // @ts-expect-error ts-migrate(2339) FIXME: Property 'updateLayerPosition' does not exist on t... Remove this comment to see the full error message
      this.refs.trigger.updateLayerPosition();
    }
    if (this.state.highlightIndex !== prevState.highlightIndex) {
      this.props.onHighlightIndexChange(this.state.highlightIndex);
    }
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.hideLayer.bind(this), false);
  }

  handleKeyDown(event: KeyboardEvent) {
    const { keyCode } = event;
    switch (keyCode) {
      case KeyConstants.DOWN:
      case KeyConstants.UP: {
        const { highlightIndex } = this.state;
        if (keyCode === KeyConstants.UP) {
          this.setState({
            highlightIndex: highlightIndex > 0 ? highlightIndex - 1 : this.props.results.length - 1,
          });
        } else {
          this.setState({
            highlightIndex: highlightIndex + 1 < this.props.results.length ? highlightIndex + 1 : 0,
          });
        }
        this.showLayer();
        break;
      }
      case KeyConstants.ENTER:
        if (this.state.highlightIndex >= 0) {
          this.selectItem(this.props.results[this.state.highlightIndex]);
        } else {
          this.props.onEnter();
        }
        break;
      default:
        this.showLayer();
        break;
    }
  }

  handleInputFocus() {
    setTimeout(() => this.showLayer(), 0);
  }

  showLayer() {
    // @ts-expect-error ts-migrate(2339) FIXME: Property 'isLayerShown' does not exist on type 'Re... Remove this comment to see the full error message
    if (!this.refs.trigger || this.refs.trigger.isLayerShown()) {
      return;
    }
    // @ts-expect-error ts-migrate(2339) FIXME: Property 'showLayer' does not exist on type 'React... Remove this comment to see the full error message
    this.refs.trigger.showLayer();
  }

  hideLayer() {
    // @ts-expect-error ts-migrate(2339) FIXME: Property 'isLayerShown' does not exist on type 'Re... Remove this comment to see the full error message
    if (!this.refs.trigger || !this.refs.trigger.isLayerShown()) {
      return;
    }
    // @ts-expect-error ts-migrate(2339) FIXME: Property 'hideLayer' does not exist on type 'React... Remove this comment to see the full error message
    this.refs.trigger.hideLayer();
    this.resetHighlight();
  }

  resetHighlight() {
    if (this.state.highlightIndex >= 0) {
      this.setState({ highlightIndex: -1 });
    }
  }

  selectItem(datum: TokenItem) {
    this.resetHighlight();
    this.props.onSelect(datum);
  }

  selectAll() {
    this.resetHighlight();
    this.props.onSelectAll();
  }

  renderInput() {
    const input = React.Children.only(this.props.children);

    // @ts-expect-error ts-migrate(2769) FIXME: No overload matches this call.
    return React.cloneElement(input, {
      // @ts-expect-error ts-migrate(2532) FIXME: Object is possibly 'undefined'.
      onFocus: createChainedFunction(() => this.handleInputFocus(), input.props.onFocus),
    });
  }

  renderList() {
    const { query, hideResults } = this.props;
    if (hideResults) {
      return null;
    }

    const items = this.props.results.map((datum: TokenItem, index) => {
      const item = this.renderListItem(datum, index === this.state.highlightIndex, query);
      return React.cloneElement(item, {
        key: datum.id,
        onMouseEnter: createChainedFunction(() => {
          if (index !== this.state.highlightIndex) {
            this.setState({ highlightIndex: index });
          }
        }, item.props.onMouseEnter),
        onMouseLeave: createChainedFunction(() => {
          if (index === this.state.highlightIndex) {
            this.setState({ highlightIndex: -1 });
          }
        }, item.props.onMouseLeave),
      });
    });

    if (items.length === 0) {
      // @ts-expect-error ts-migrate(2345) FIXME: Argument of type 'ReactElement<any, string | JSXEl... Remove this comment to see the full error message
      items.push(query ? this.props.noResultsRenderer(query) : this.props.noMoreItemsRenderer());
    }

    return this.props.listRenderer(compact(items), this.props.loading || false);
  }

  renderListItem(
    item: TokenItem | HeaderItem,
    highlighted: boolean,
    query: string
  ): React.ReactElement<any> {
    const {
      selectAllItemRenderer,
      headerRenderer,
      useCustomSelectionHandler,
      itemRenderer,
      onSelect,
    } = this.props;

    if (typeof item.id === 'string') {
      if (item === ALL_TOKENS_ITEM) {
        invariant(selectAllItemRenderer, 'ALL_TOKENS_ITEM without renderer');
        // @ts-expect-error ts-migrate(2722) FIXME: Cannot invoke an object which is possibly 'undefin... Remove this comment to see the full error message
        return selectAllItemRenderer(highlighted, () => this.selectAll());
      } else if (item.id.startsWith(HEADER_ITEM_ID)) {
        invariant(headerRenderer, 'HEADER_ITEM without renderer');
        // $FlowFixMe 'type' property not found?
        const headerItem = item as HeaderItem;
        // @ts-expect-error ts-migrate(2722) FIXME: Cannot invoke an object which is possibly 'undefin... Remove this comment to see the full error message
        return headerRenderer(headerItem);
      }
    }

    const selectionHandler = useCustomSelectionHandler ? onSelect : () => this.selectItem(item);
    return itemRenderer(item, highlighted, query, selectionHandler);
  }

  renderLayer() {
    const { manageLayer } = this.props;
    return (
      <Layer
        hideOnBlur={manageLayer}
        style={{ margin: '0 44px' }}
        onMouseLeave={() => this.resetHighlight()}
        onRequestHide={() => this.resetHighlight()}
      >
        {this.renderList()}
      </Layer>
    );
  }

  render() {
    const props = { ...this.props, onSelect: null };
    const { inline, className, manageLayer } = props;

    if (inline) {
      return (
        // @ts-expect-error ts-migrate(2322) FIXME: Type 'string | null | undefined' is not assignable... Remove this comment to see the full error message
        <div className={className}>
          {this.renderInput()}
          {this.renderList()}
        </div>
      );
    }

    return (
      <LayerTrigger
        ref="trigger"
        // @ts-expect-error ts-migrate(2322) FIXME: Type 'string | null | undefined' is not assignable... Remove this comment to see the full error message
        className={className}
        layer={this.renderLayer()}
        trigger="manual"
        placement="bottom"
        useContextWidth
        fixedPlacement={!manageLayer}
      >
        {this.renderInput()}
      </LayerTrigger>
    );
  }
}

export default Typeahead;
